import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useBriefState } from '../lib/brief-state';

type ClassifyVerdict = 'cleared' | 'borderline' | 'refused';
interface ClassifyResult {
  verdict: ClassifyVerdict;
  reason?: string;
  categories?: string[];
}

const HOLD_DURATION_MS = 2000;

export default function PressMoment() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session, load } = useBriefState();
  const [pressing, setPressing] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [classifyResult, setClassifyResult] = useState<ClassifyResult | null>(null);
  const [classifying, setClassifying] = useState(false);
  const holdTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const holdStart = useRef<number>(0);

  useEffect(() => {
    if (id && (!session || session.sessionId !== id)) {
      load(id);
    }
  }, [id, session, load]);

  const startHold = useCallback(() => {
    holdStart.current = Date.now();
    setHoldProgress(0);
    holdTimer.current = setInterval(() => {
      const elapsed = Date.now() - holdStart.current;
      const progress = Math.min(elapsed / HOLD_DURATION_MS, 1);
      setHoldProgress(progress);
      if (progress >= 1) {
        if (holdTimer.current) clearInterval(holdTimer.current);
        triggerPress();
      }
    }, 16);
  }, []);

  const cancelHold = useCallback(() => {
    if (holdTimer.current) clearInterval(holdTimer.current);
    setHoldProgress(0);
  }, []);

  const runClassify = useCallback(async () => {
    if (!session) return;
    setClassifying(true);
    setError(null);
    try {
      const res = await fetch(`/api/brief/${session.sessionId}/classify`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        setError('Content check failed. Try again.');
        setClassifying(false);
        return;
      }
      const result: ClassifyResult = await res.json();
      setClassifying(false);
      if (result.verdict === 'cleared') {
        doPress();
      } else {
        setClassifyResult(result);
      }
    } catch {
      setError('Connection error during content check');
      setClassifying(false);
    }
  }, [session]);

  const doPress = useCallback(async () => {
    if (!session) return;
    setPressing(true);
    setError(null);
    try {
      const res = await fetch(`/api/brief/${session.sessionId}/press`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Failed to start press' }));
        setError(data.error ?? 'Failed to start press');
        setPressing(false);
        return;
      }
      navigate(`/brief/${session.sessionId}/status`);
    } catch {
      setError('Connection error');
      setPressing(false);
    }
  }, [session, navigate]);

  const triggerPress = useCallback(async () => {
    runClassify();
  }, [runClassify]);

  if (!session || session.sessionId !== id) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const approved = session.sections.filter((s) => s.status === 'approved');
  const generated = session.sections.filter((s) => s.status === 'generated');
  const failed = session.sections.filter((s) => s.status === 'failed');
  const warnings = session.cascadeWarnings?.filter((w) => !w.dismissed) ?? [];
  const allApproved = approved.length === session.sections.length;
  const canPress = allApproved && warnings.length === 0 && !pressing;

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-5 sm:px-8 py-3.5 border-b border-border">
        <a href="/" className="font-display text-[17px] text-foreground flex items-center gap-2">
          <span className="w-[7px] h-[7px] rounded-full bg-primary inline-block" aria-hidden="true" />
          Ghostwright
        </a>
        <button
          onClick={() => navigate(`/brief/${id}`)}
          className="text-xs text-muted-foreground hover:text-foreground cursor-pointer border-none bg-transparent"
        >
          Back to canvas
        </button>
      </header>

      <main className="max-w-[560px] mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="text-[10px] tracking-[0.14em] uppercase text-placeholder mb-1">
          <span className="text-primary/60" aria-hidden="true">§</span> Press Moment
        </div>
        <h1 className="font-display text-[28px] font-normal italic text-heading mb-8 [font-variation-settings:'opsz'_96,'SOFT'_60]">
          Ready to press?
        </h1>

        <section className="mb-8">
          <h2 className="text-sm font-semibold text-foreground mb-3">Completeness Report</h2>
          <div className="space-y-2">
            <ReportRow label="Approved" count={approved.length} total={session.sections.length} status="success" />
            {generated.length > 0 && (
              <ReportRow label="Awaiting approval" count={generated.length} total={session.sections.length} status="warn" />
            )}
            {failed.length > 0 && (
              <ReportRow label="Failed" count={failed.length} total={session.sections.length} status="error" />
            )}
          </div>
        </section>

        {warnings.length > 0 && (
          <section className="mb-8">
            <h2 className="text-sm font-semibold text-foreground mb-3">Outstanding Warnings</h2>
            <ul className="space-y-2">
              {warnings.map((w, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary mt-1 flex-shrink-0" />
                  <span>{w.reason}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-placeholder mt-2">
              Resolve these on the canvas before pressing.
            </p>
          </section>
        )}

        {!allApproved && (
          <section className="mb-8 p-4 bg-surface border border-border rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground font-medium">{session.sections.length - approved.length} sections</strong> still need your approval before pressing.
            </p>
            <button
              onClick={() => navigate(`/brief/${id}`)}
              className="mt-3 text-xs font-medium text-primary cursor-pointer border-none bg-transparent"
            >
              Return to canvas
            </button>
          </section>
        )}

        {error && (
          <p className="text-sm text-destructive mb-4">{error}</p>
        )}

        {classifyResult?.verdict === 'refused' && (
          <section className="mb-8 p-4 bg-surface border border-destructive/30 rounded-lg">
            <h2 className="text-sm font-semibold text-destructive mb-2">Content not permitted</h2>
            <p className="text-sm text-muted-foreground mb-3">{classifyResult.reason}</p>
            <button
              onClick={() => { setClassifyResult(null); navigate(`/brief/${id}`); }}
              className="text-xs font-medium text-primary cursor-pointer border-none bg-transparent"
            >
              Return to canvas and revise
            </button>
          </section>
        )}

        {classifyResult?.verdict === 'borderline' && (
          <section className="mb-8 p-4 bg-surface border border-primary/30 rounded-lg">
            <h2 className="text-sm font-semibold text-foreground mb-2">Content advisory</h2>
            <p className="text-sm text-muted-foreground mb-4">{classifyResult.reason}</p>
            <p className="text-xs text-placeholder mb-4">
              You can still press this book. The advisory is informational.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setClassifyResult(null); doPress(); }}
                className="text-xs font-semibold text-primary cursor-pointer border-none bg-transparent py-1"
              >
                Proceed anyway
              </button>
              <button
                onClick={() => { setClassifyResult(null); navigate(`/brief/${id}`); }}
                className="text-xs font-medium text-muted-foreground cursor-pointer border-none bg-transparent py-1"
              >
                Go back and revise
              </button>
            </div>
          </section>
        )}

        {!classifyResult && (
          <div className="pt-4">
            <div className="relative">
              <button
                onMouseDown={canPress ? startHold : undefined}
                onMouseUp={cancelHold}
                onMouseLeave={cancelHold}
                onTouchStart={canPress ? startHold : undefined}
                onTouchEnd={cancelHold}
                disabled={!canPress || classifying}
                className="relative w-full py-4 px-6 bg-primary text-white border-none rounded-md font-body text-sm font-semibold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed overflow-hidden"
              >
                <span
                  className="absolute inset-0 bg-white/20 origin-left transition-transform"
                  style={{ transform: `scaleX(${holdProgress})` }}
                />
                <span className="relative">
                  {classifying ? 'Checking content...' : pressing ? 'Starting press...' : holdProgress > 0 ? 'Hold to confirm...' : 'Press My Book'}
                </span>
              </button>
              <p className="text-[10px] text-placeholder text-center mt-2">
                Hold for 2 seconds to confirm. This uses 1 credit.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function ReportRow({ label, count, total, status }: { label: string; count: number; total: number; status: 'success' | 'warn' | 'error' }) {
  const colors = {
    success: 'bg-success',
    warn: 'bg-primary',
    error: 'bg-destructive',
  };
  return (
    <div className="flex items-center gap-3">
      <span className={`w-2 h-2 rounded-full ${colors[status]}`} />
      <span className="text-sm text-foreground">{label}</span>
      <span className="text-sm text-muted-foreground ml-auto">{count}/{total}</span>
    </div>
  );
}
